import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter, Switch, Route, BrowserRouterProps, RouteProps } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@material-ui/core/styles';
import { ThemeProvider } from 'styled-components';
import usePromptNetwork from './hooks/useNetworkPrompt';
import BanksProvider from './contexts/Banks';
import TombFinanceProvider from './contexts/TombFinanceProvider';
import ModalsProvider from './contexts/Modals';
import store from './state';
import theme from './theme';
import { config } from "./configg";
import newTheme from './newTheme';
import Updaters from './state/Updaters';
import Loader from './components/Loader';
import Popups from './components/Popups';
import Regulations from './views/Regulations/Regulations';
import { keepTheme } from './utils/themeSwitchV2/Themes';
import { RefreshContextProvider } from './contexts/RefreshContext';
import { WagmiConfig } from 'wagmi';
import { RainbowKitProvider, darkTheme } from '@rainbow-me/rainbowkit';
import { WagmiProvider } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import '@rainbow-me/rainbowkit/styles.css';
import ComingSoon from './views/ComingSoon';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Bank from './views/Bank';
import Bank2 from './views/Bank2';
import Pit from './views/Pit';
import useTotalValueLocked from './hooks/useTotalValueLocked';
import AutoCompounders from './contexts/AutoCompounders';
import AutoCompounder from './views/AutoCompounder';
const Home = lazy(() => import('./views/Home'));
const Cemetery = lazy(() => import('./views/Cemetery'));
const Portfolio = lazy(() => import('./views/Portfolio'));

const Masonry = lazy(() => import('./views/Masonry'));
const Sanctum = lazy(() => import('./views/MasonryV2'));
const SanctumV3 = lazy(() => import('./views/MasonryV3'));

// const Pit = lazy(() => import('./views/Pit'));
// const SBS = lazy(() => import('./views/Sbs'));
const Liquidity = lazy(() => import('./views/Liquidity'));
const Obelisk = lazy(() => import('./views/Obelisk'));
const Daas = lazy(() => import('./views/Daas'));
const queryClient = new QueryClient();

// Define interfaces for all providers
interface ProviderProps {
  children: React.ReactNode;
}

interface ThemeProviderProps extends ProviderProps {
  theme: any;
}

interface ReduxProviderProps extends ProviderProps {
  store: any;
}

interface RainbowKitProviderProps extends ProviderProps {
  // Add any additional props needed for RainbowKitProvider
}

interface WagmiProviderProps extends ProviderProps {
  config: any;
}

interface QueryClientProviderProps extends ProviderProps {
  client: any;
}

// Type assertions for providers
const SafeBrowserRouter = BrowserRouter as unknown as React.ComponentType<ProviderProps>;
const SafeSwitch = Switch as unknown as React.ComponentType<ProviderProps>;
const SafeRoute = Route as unknown as React.ComponentType<RouteProps>;
const SafeThemeProvider = ThemeProvider as unknown as React.ComponentType<ThemeProviderProps>;
const SafeReduxProvider = ReduxProvider as unknown as React.ComponentType<ReduxProviderProps>;
const SafeRefreshContextProvider = RefreshContextProvider as unknown as React.ComponentType<ProviderProps>;
const SafeTombFinanceProvider = TombFinanceProvider as unknown as React.ComponentType<ProviderProps>;
const SafeModalsProvider = ModalsProvider as unknown as React.ComponentType<ProviderProps>;
const SafeBanksProvider = BanksProvider as unknown as React.ComponentType<ProviderProps>;
const SafeRainbowKitProvider = RainbowKitProvider as unknown as React.ComponentType<RainbowKitProviderProps>;
const SafeWagmiProvider = WagmiProvider as unknown as React.ComponentType<WagmiProviderProps>;
const SafeQueryClientProvider = QueryClientProvider as unknown as React.ComponentType<QueryClientProviderProps>;
const SafeAutoCompoundersProvider = AutoCompounders as unknown as React.ComponentType<ProviderProps>;

const NoMatch = () => (
  <h3 style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
    URL Not Found. <a href="/">Go back home.</a>
  </h3>
);

// Add this new component
const TVLData: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>(() => {
    return localStorage.getItem('tvl_last_updated') || 'Never';
  });
  const tvl = useTotalValueLocked();

  useEffect(() => {
    if (tvl !== 0) {
      setLoading(false);
      const now = new Date().toLocaleString();
      localStorage.setItem('tvl_last_updated', now);
      setLastUpdated(now);
    }
  }, [tvl]);

  return (
    <div style={{ color: 'white', padding: 20 }}>
      <pre style={{ margin: 0 }}>
        {JSON.stringify({
          tvl,
          last_updated: lastUpdated,
          status: loading ? 'Refreshing...' : 'Live'
        }, null, 2)}
      </pre>
    </div>
  );
};

const App: React.FC = () => {
  // Clear localStorage for mobile users
  if (typeof localStorage.version_app === 'undefined' || localStorage.version_app !== '1.1') {
    localStorage.clear();
    localStorage.setItem('connectorId', '');
    localStorage.setItem('version_app', '1.1');
  }

  useEffect(() => {
    keepTheme();
    // Add background color to body
    document.body.style.backgroundColor = 'hsl(var(--sonic-black))';
  }, [])
  usePromptNetwork();

  return (
    <>
      <ToastContainer />
      <div className="bg-sonic-black min-h-screen">
        <SafeWagmiProvider config={config}>
          <SafeQueryClientProvider client={queryClient}>
            <SafeRainbowKitProvider
            >
              <Providers>
                <SafeBrowserRouter>
                  <Suspense fallback={<Loader />}>
                    <SafeSwitch>
                      <SafeRoute exact path="/">
                        <Home />
                      </SafeRoute>
                      <SafeRoute path="/elysium">
                        <Cemetery />
                      </SafeRoute>
                      <SafeRoute path="/sanctum-v1">
                        <Masonry />
                      </SafeRoute>
                      <SafeRoute path="/sanctumv2">
                        <Sanctum />
                      </SafeRoute>
                      <SafeRoute path="/sanctumv3">
                        <SanctumV3 />
                      </SafeRoute>
                      <SafeRoute path={`/genesisPool/:bankId`}>
                        <Bank />
                      </SafeRoute>
                      <SafeRoute path={`/farms/:bankId`}>
                        <Bank2 />
                      </SafeRoute>
                      <SafeRoute path={`/autocompounders/:autoCompounderId`}>
                        <AutoCompounder />
                      </SafeRoute>
                      <SafeRoute path={`/aidensgarden`}>
                        <Daas />
                      </SafeRoute>
                      <SafeRoute path={`/aidensgardenghog`}>
                        <Daas />
                      </SafeRoute>
                      <SafeRoute path={`/portfolio`}>
                        <Portfolio />
                      </SafeRoute>
                      {/* <Route path="/desert">
                        <Pit />
                      </Route>
                      <Route path="/swap">
                        <SBS />
                      </Route> */}
                      <SafeRoute path="/purgatory">
                        <Obelisk />
                      </SafeRoute>

                      <SafeRoute path="/scriptures">
                        <Regulations />
                      </SafeRoute>
                      {/* <Route path="/liquidity">
                        <Liquidity />
                      </Route> */}
                      {/* 
                  
                      
                    */}
                      {/* <Route path="/token-jackpot">
                        <TokenJackpotGameMain/>
                      </Route> */}

                      <SafeRoute exact path="/api/tvl">
                        <TVLData />
                      </SafeRoute>

                      <SafeRoute path="*">
                        <NoMatch />
                      </SafeRoute>
                    </SafeSwitch>
                  </Suspense>
                </SafeBrowserRouter>
              </Providers>
            </SafeRainbowKitProvider>
          </SafeQueryClientProvider>
        </SafeWagmiProvider>
      </div>
    </>
  );
};

const Providers: React.FC<ProviderProps> = ({ children }) => {
  return (
    <SafeThemeProvider theme={theme}>
      <MuiThemeProvider theme={newTheme}>
        <SafeReduxProvider store={store}>
          <Updaters />
          <SafeRefreshContextProvider>
            <SafeTombFinanceProvider>
              <SafeModalsProvider>
                <SafeBanksProvider>
                  <SafeAutoCompoundersProvider>
                    <>
                      <Popups />
                      {children}
                    </>
                  </SafeAutoCompoundersProvider>
                </SafeBanksProvider>
              </SafeModalsProvider>
            </SafeTombFinanceProvider>
          </SafeRefreshContextProvider>
        </SafeReduxProvider>
      </MuiThemeProvider>
    </SafeThemeProvider>
  );
};

export default App;
